import {
  AdminDollarInput2,
  AdminInput,
  AdminPhoneInput,
  AdminSelect,
  Button,
  Div,
  ErrorToast,
  Modal,
  PropsModal,
  SuccessToast,
  updateUserById,
  uprateAgentFlatMountByUser,
  reportError,
} from '@vgrubs/components';
import { AgentDetailsResponse } from 'components/src/types/api/agents.types';
import {
  GetAllUsersResponse,
  UpdateUserRequestData,
} from 'components/src/types/api/user.types';
import { User, UserRole } from 'components/src/types/user.types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

export const UsersAndAccessEditModal = ({
  modalIsOpen,
  setLoading,
  setUsers,
  closeModal,
  setUserDetailsModal,
  isAgent = false,
  agentAmountPerLocation,
  setAgentDetails,
  onSuccess,
}: PropsModal & {
  modalIsOpen: User | null;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setUserDetailsModal?: Dispatch<SetStateAction<User | null>>;
  setUsers?: Dispatch<SetStateAction<GetAllUsersResponse[]>>;
  isAgent?: boolean;
  agentAmountPerLocation?: number;
  setAgentDetails?: Dispatch<SetStateAction<AgentDetailsResponse | null>>;
  onSuccess?: () => void;
}) => {
  const user = modalIsOpen;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [role, setRole] = useState<UserRole | null>(null);
  const [amountPerLocation, setAmountPerLocation] = useState(0);
  // const [achCustomerId, setAchCustomerId] = useState('');

  const showRolePicker = role === UserRole.admin || role === UserRole.support;

  const roles = [
    { name: 'Admin', value: UserRole.admin },
    { name: 'Support', value: UserRole.support },
  ];

  const typeOptions = (
    <optgroup label="Select Role">
      {roles.map((r, i) => (
        <option value={r.value} key={i}>
          {r.name}
        </option>
      ))}
    </optgroup>
  );

  async function handleUpdateAgentFlatAmount() {
    if (!user) return;
    try {
      setLoading(true);

      await uprateAgentFlatMountByUser(user.id, amountPerLocation);

      setAgentDetails?.((prev) => {
        if (!prev) return null;
        return { ...prev, agent: { ...prev.agent, amountPerLocation } };
      });

      // SuccessToast(data.message);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyLocation() {
    if (!user) return;
    if (!firstName) {
      ErrorToast('Please enter a first name');
      return;
    }
    if (!lastName) {
      ErrorToast('Please enter a last name');
      return;
    }
    if (!email) {
      ErrorToast('Please enter an email');
      return;
    }
    if (!phoneNumber) {
      ErrorToast('Please enter a phone number');
      return;
    }

    const body: UpdateUserRequestData = {
      firstName,
      lastName,
      phoneNumber,
      role: role || undefined,
      email,
    };

    try {
      setLoading(true);
      const { data } = await updateUserById(user.id, body);
      SuccessToast(data.message);

      if (!isAgent) {
        setUserDetailsModal?.(data.data);
        setUsers?.((prevState) => {
          // Map over the previous state array
          return prevState.map((item) => {
            // If the ID matches, update the properties
            if (item.user.id === user.id) {
              return { ...item, user: data.data };
            }
            // If the ID doesn't match, return the original item
            return item;
          });
        });
      } else {
        setAgentDetails?.((prev) => {
          if (!prev) return null;
          return { ...prev, user: data.data };
        });
        if (
          isAgent &&
          amountPerLocation &&
          agentAmountPerLocation !== amountPerLocation
        ) {
          setAgentDetails?.((prev) => {
            if (!prev) return null;
            return { ...prev, user: data.data };
          });
          handleUpdateAgentFlatAmount();
        }
      }

      onSuccess?.();
      closeModal?.();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (modalIsOpen) {
      setFirstName(user?.firstName || '');
      setLastName(user?.lastName || '');
      setEmail(user?.email || '');
      setPhoneNumber(user?.phoneNumber || '');
      setRole(user?.role || null);
      if (isAgent && agentAmountPerLocation !== undefined) {
        setAmountPerLocation(agentAmountPerLocation);
      }
    }
  }, [modalIsOpen]);

  const handleFlatAmount = useCallback((val) => {
    setAmountPerLocation(val);
  }, []);

  return (
    <Modal
      isOpen={!!modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header="Edit User"
      zIndex={110}
    >
      <Div $flex $column $gap="20px" $gapSM="16px">
        <AdminInput
          label="First Name"
          placeholder="Enter here"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          $textCenter
        />
        <AdminInput
          label="Last Name"
          placeholder="Enter here"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          $textCenter
        />
        <AdminInput
          label="Email"
          placeholder="Enter here"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          $textCenter
        />

        <AdminPhoneInput
          value={phoneNumber}
          onChange={setPhoneNumber}
          label="Phone"
          $textCenter
        />

        {isAgent && (
          <AdminDollarInput2
            label="Flat Amount Per Location"
            onValueChange={handleFlatAmount}
            value={amountPerLocation}
            $textCenter
          />
        )}

        {showRolePicker && (
          <AdminSelect
            value={role}
            options={typeOptions}
            onChange={({ target }) => setRole(target.value as UserRole)}
            placeholder="Select location"
            label="Role"
            $textCenter
          />
        )}
      </Div>

      <Button
        $selected
        $w="100%"
        $mt="50"
        $mtSM="30"
        onClick={handleVerifyLocation}
      >
        Edit
      </Button>
    </Modal>
  );
};
